import { makeAutoObservable, runInAction } from "mobx";
import { IPolicyRule, IReturnReason } from "../../../models/ReturnReasonTypes";
import ReturnReasonsService from "../../../services/api/return-reasons-service";
import { rootContainer, TYPE } from "../../../services/container/root";
import {ISaveable, WithOld} from "../../../stores/types";
import { RETURN_REASON_TYPE } from "../constants";
import FollowUpQuestionStore from "./follow-up-question-store";
import { IReasonListStore } from "./reason-list-store";
import { generateQuestion } from "./follow-up-question-model";
import { uuid } from "../../../services/helpers";
import { ITranslationScopeContext } from "../../../translation/translation-scope";


interface IReturnReasonChanges extends Partial<IReturnReason>{};

class ReasonStore implements ISaveable {
    private _changes: IReturnReasonChanges = {};
    private _model: IReturnReason = {} as IReturnReason;
    private _fuQuestions: FollowUpQuestionStore[] = [];
    private _isSaving: boolean = false;
    private _clientId?: string;

    private _currentTranslationKeys: string[] = [];

    private readonly _translatableFields: string[] = [
        'displayName'
    ];

    constructor(
        private _reasonListStore: IReasonListStore,
        private _discardReason: (store: ReasonStore) => void,
        model: IReturnReason = {} as IReturnReason,
        clientId?: string
    ) {
        this._clientId = clientId;
        this._discardQuestion = this._discardQuestion.bind(this);
        this._setModel(model);
        makeAutoObservable(this);
    }

    private get returnReasonsService(): ReturnReasonsService {
		return rootContainer.get<ReturnReasonsService>(TYPE.ReturnReasonsService);
	}

    private _setModel(model: IReturnReason): void {
        this._model = model;
        this._fuQuestions = this._model.follow_up_questions?.map(questionModel => {
            const question = new FollowUpQuestionStore(this._model.id, this._discardQuestion, questionModel);
            return question;
        }) ?? [];
        this._changes = {} as IReturnReasonChanges;
    }

    public setChanges(changes: IReturnReasonChanges): void {
        this._changes = changes;
        this._fuQuestions = this._changes.follow_up_questions?.map(questionModel => {
            const question = new FollowUpQuestionStore(this.id, this._discardQuestion);
            question.setChanges(questionModel);
            return question;
        }) ?? [];
    }

    public isIdEquals(id?:string|number): boolean {
        return this._model.id === Number(id) || this._clientId === (id ?? '').toString();
    }

    public get followUpQuestions() { return this._fuQuestions.slice().sort((a, b) => a.weight - b.weight); }
    public get type(): RETURN_REASON_TYPE { return this._changes.type ?? this._model.type; }
    public get displayName() { return this._changes.display_name ?? this._model.display_name; }
    public get externalReasonCode() { return this._changes.external_reason_code ?? this._model.external_reason_code; }
    public get globalECode() { return this._changes.global_e_code ?? this._model.global_e_code; }
    public get id(): number|string { return this._model.id ?? this._clientId; }
    public get isEnabled() { return this._changes.is_enabled ?? this._model.is_enabled; }
    public get weight() { return this._changes.weight ?? this._model.weight; }
    public get deleted() { return this._changes._deleted ?? false; }
    public get isSwitchToExchangeEnabled() { return this._changes.is_eligible_for_switch_to_exchange ?? this._model.is_eligible_for_switch_to_exchange; }
    public get exchangeTypes() { return this._changes.exchange_types ?? this._model.exchange_types; }
    public isSaving(): boolean { return this._isSaving; }

    public setType(val:RETURN_REASON_TYPE) {
        this._changes.type = val;
    }

    public setExternalReasonCode(val:string|undefined) {
        this._changes.external_reason_code = val;
    }

    public setGlobalECode(val:string|undefined) {
        this._changes.global_e_code = val;
    }

    public setIsEnabled(val:boolean) {
        this._changes.is_enabled = val;
    }

    public setWeight(val:number) {
        this._changes.weight = val;
    }

    public setIsSwitchToExchangeEnabled(val:boolean) {
        this._changes.is_eligible_for_switch_to_exchange = val;
    }

    public setExchangeTypes(val: string) {
        this._changes.exchange_types = val;
    }

    public onExchangeTypeChange(exchangeType: string) {
        const exchangeTypesArr = this.exchangeTypes?.split(',') ?? [];
        const index = exchangeTypesArr.indexOf(exchangeType);
        if (index !== -1) {
            exchangeTypesArr.splice(index, 1);
        } else {
            exchangeTypesArr.push(exchangeType);
        }
        this.setExchangeTypes(exchangeTypesArr.join(','));
    }

    public getNewWeight() : number {
        if (this._fuQuestions.length === 0) {
            return 1;
        }

        return Math.max(...this._fuQuestions.map(q => q.weight)) + 1;
    }

    public setDeleted(setDeletedTranslationKeys: ITranslationScopeContext['setDeletedKeys']) {
        const keyList = this.getTranslationKeys();
        setDeletedTranslationKeys(keyList);

        this._changes._deleted = true;

        this._fuQuestions.forEach(store => {
            store.setDeleted(setDeletedTranslationKeys);
        });
    }

    public getTranslationKeys(): string[] {
        const keyList: string[] = [];

        /** commented out, since will be implemented with delete reason feature */
        if (this.deleted) {
            return keyList;
        }

        this._translatableFields.forEach(field => {
            const key = this[field as keyof ReasonStore];
            if (key) {
                keyList.push(key.toString());
            }
        });
        this._fuQuestions.forEach(qStore => {
            keyList.push(...qStore.getTranslationKeys());
        });

        /** we need this._currentTranslationKeys property to use it as dependency in react hooks,
         * so that the hook method will run only when the list was changed */
        if (this._currentTranslationKeys.length !== keyList.length || !this._currentTranslationKeys.every(k => !!keyList.find(key => key === k))) {
            runInAction(() => {
                this._currentTranslationKeys = keyList;
            });
        }

        return this._currentTranslationKeys;
    }

    public addFollowUpQuestion():string {
        const clientId = uuid();
        const newWeight = this.getNewWeight();

        const questionStore = new FollowUpQuestionStore(this.id, this._discardQuestion);
        const changesModel = generateQuestion(clientId, undefined, newWeight);
        questionStore.setChanges(changesModel);
        this._fuQuestions.push(questionStore);
        return clientId;
    }

    /** preventing from disabling last reason */
    public canDisable(): boolean {
        const enabledCount =  this._reasonListStore.returnReasons.filter(store => store.isEnabled).length;
        return enabledCount > 1;
    }

    /** ISaveable implementation */
    public hasChanges(): boolean {
        const hasQuestionsChanges = this._fuQuestions.some(store => store.hasChanges());
        return !!Object.keys(this._changes).length || hasQuestionsChanges;
    }

    private getUpdatedModel(): IReturnReason {
        return {
            ...{ ...this._model, ...this._changes },
            follow_up_questions: this._fuQuestions?.map(store => store.getUpdatedModel()),
        };
    }

    public async save(): Promise<void> {
        const updatedModel: WithOld<IReturnReason> = this.getUpdatedModel();
        console.log('save return reason', updatedModel);

        let savedReason: IReturnReason|undefined = undefined;
        if (updatedModel.id) {
            updatedModel.__old = this._model;
            savedReason = await this.returnReasonsService.updateReason(updatedModel.id, updatedModel);
        } else {
            savedReason = await this.returnReasonsService.createReason(updatedModel);
        }

        runInAction(() => {
            this._setModel(savedReason as IReturnReason);
        });
    }

    public async delete(): Promise<IPolicyRule[]> {
        return await this.returnReasonsService.deleteReason(this._model.id);
    }

    public discardChanges():void {
        if (!this._model.id) {
            this._discardReason(this);
        }
        else {
            this._changes = {} as IReturnReason;
            [...this._fuQuestions].forEach(store => store.discardChanges());
        }
    }

    /** removes newly created question store after it was discarded */
    private _discardQuestion(store: FollowUpQuestionStore): void {
        const storeIndex = this._fuQuestions.findIndex(s => s === store);
        if (storeIndex < 0) {
            /** should never happen */
            throw new Error('discardQuestion did not find the store to remove');
        }
        this._fuQuestions.splice(storeIndex, 1);
    }

    public commitChanges() {
        this._model = {...this._model, ...this._changes};
        this._changes = {} as IReturnReason;
    }
}

export default ReasonStore;
